/**
 * Papéis / tipos de usuário da plataforma.
 * Valores observados no backend/front: ADM (Admin), ENG, Regular, PROP, GUARDIAN
 * (aceita também os aliases ADMIN e GUARD).
 */

const ALL_PROJECT_TAB_LABELS = [
  "Informações",
  "Administrativo",
  "Ambiental",
  "Comercial",
  "Jurídico",
];

/** Abas liberadas para ENG na interna do projeto. */
const ENG_PROJECT_TAB_LABELS = ["Informações", "Ambiental"];

export function getUserType(user) {
  if (!user) return "";
  return String(user.user_type || user?.user?.user_type || "").trim().toUpperCase();
}

/** Admin da plataforma — tipo canônico: ADM (alias: ADMIN). */
export function isAdminUser(user) {
  const type = getUserType(user);
  return type === "ADM" || type === "ADMIN";
}

export function isEngineeringUser(user) {
  const type = getUserType(user);
  return type === "ENG" || type === "ENGINEERING" || type === "ENGENHARIA";
}

/** Proprietário ou Guardian de área (PROP / GUARDIAN / GUARD). */
export function isPropOrGuardianUser(user) {
  const type = getUserType(user);
  return type === "PROP" || type === "GUARDIAN" || type === "GUARD";
}

/** ADM e ENG veem a aba Usuários. */
export function canAccessUsersPanel(user) {
  return isAdminUser(user) || isEngineeringUser(user);
}

/** Gestão plena (criar qualquer tipo, excluir, editar todos) — somente ADM. */
export function canManageUsers(user) {
  return isAdminUser(user);
}

/** ENG (ou ADM) pode cadastrar PROP/GUARDIAN de forma simplificada. */
export function canCreateFieldUsers(user) {
  return isAdminUser(user) || isEngineeringUser(user);
}

/**
 * Pode editar o registro alvo na tela de usuários.
 * - ADM: qualquer um
 * - ENG: somente PROP / GUARDIAN
 */
export function canEditUserRecord(currentUser, targetUser) {
  if (!currentUser || !targetUser) return false;
  if (isAdminUser(currentUser)) return true;
  if (isEngineeringUser(currentUser) && isPropOrGuardianUser(targetUser)) {
    return true;
  }
  return false;
}

/** Tipos que o ENG enxerga na listagem (alinhado ao backend). */
export const ENG_VISIBLE_USER_TYPES = [
  "ADM",
  "ADMIN",
  "ENG",
  "COM",
  "GUARDIAN",
  "GUARD",
  "PROP",
];

export function filterUsersForViewer(users, currentUser) {
  const list = Array.isArray(users) ? users : [];
  if (!isEngineeringUser(currentUser) || isAdminUser(currentUser)) {
    return list;
  }
  const allowed = new Set(ENG_VISIBLE_USER_TYPES);
  return list.filter((u) => allowed.has(getUserType(u)));
}

/**
 * ADM e ENG têm o mesmo alcance operacional na plataforma,
 * exceto as abas Administrativo / Comercial / Jurídico (só ADM).
 */
export function isAdminOrEngineeringUser(user) {
  return isAdminUser(user) || isEngineeringUser(user);
}

export function canAccessDashboard(user) {
  return !isPropOrGuardianUser(user);
}

/** ADM + ENG + PROP + Guardian veem o item Guardian na navegação. */
export function canAccessGuardian(user) {
  return (
    isAdminUser(user) ||
    isEngineeringUser(user) ||
    isPropOrGuardianUser(user)
  );
}

/** ADM, ENG e demais (exceto PROP/Guardian) podem criar alerta. */
export function canCreateGuardianAlert(user) {
  if (!user) return false;
  if (isPropOrGuardianUser(user)) return false;
  return true;
}

export function canAccessInviteWelcome(user) {
  return !isPropOrGuardianUser(user) && Boolean(user);
}

/** Alertas não respondidos / reativação de envios e carga do servidor — ADM e ENG. */
export function canReviewAlertNotifications(user) {
  return isAdminUser(user) || isEngineeringUser(user);
}

/** Lista de solicitações de alerta (aba em /view_alerts). */
export function canAccessAlertRequests(user) {
  return !isPropOrGuardianUser(user) && Boolean(user);
}

/**
 * Aba Relatórios do hub Guardian (lista global).
 * PROP/GUARD não veem: a API não filtra por projeto do usuário e o payload
 * não traz project_id — sem isso a lista misturaria relatórios de outros projetos.
 * Relatórios por área (`/alerts/history-report/:area_id`) continuam acessíveis.
 */
export function canAccessGuardianReportsTab(user) {
  return !isPropOrGuardianUser(user) && Boolean(user);
}

/**
 * Abas do projeto interno visíveis por tipo de usuário.
 * - ADM: todas (Informações, Administrativo, Ambiental, Comercial, Jurídico)
 * - ENG: somente Informações + Ambiental
 * - Regular / PROP / GUARDIAN: somente Informações (sem barra das demais abas)
 */
export function getVisibleProjectTabLabels(user) {
  const type = getUserType(user);

  if (type === "REGULAR" || isPropOrGuardianUser(user)) {
    return ["Informações"];
  }

  if (isEngineeringUser(user)) {
    return [...ENG_PROJECT_TAB_LABELS];
  }

  // ADM / ADMIN e demais papéis com acesso completo às abas
  return [...ALL_PROJECT_TAB_LABELS];
}

/** Painel inicial após login. */
export function getDefaultPanelForUser(user) {
  if (isPropOrGuardianUser(user)) return "Meus Alertas";
  return "Dashboard";
}

export function getDefaultRouteForUser(user) {
  if (isPropOrGuardianUser(user)) return "/my-alerts";
  return "/dashboard";
}
